import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { LISTER_TYPES, listerLabel } from "@/data/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, X, ShieldCheck, Clock, CircleAlert, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  verified: { label: "Verified", cls: "bg-[#DBEAFE] text-[#1D4ED8]", icon: ShieldCheck },
  pending: { label: "Under review", cls: "bg-[#FEF3C7] text-[#B45309]", icon: Clock },
  rejected: { label: "Rejected", cls: "bg-[#FEE2E2] text-[#B91C1C]", icon: CircleAlert },
  unverified: { label: "Not submitted", cls: "bg-[#F3F4F6] text-[#6B7280]", icon: CircleAlert },
};

export default function GetVerified() {
  const [me, setMe] = useState(null);
  const [role, setRole] = useState("landlord");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const loadMe = () => api.get("/verification/me").then(({ data }) => {
    setMe(data);
    if (data.verification_role) setRole(data.verification_role);
    if (data.phone) setPhone(data.phone);
    if (data.email) setEmail(data.email);
    if (data.docs?.length) setDocs(data.docs);
  }).catch(() => setMe({ verification_status: "unverified" }));

  useEffect(() => { loadMe(); }, []);

  const uploadDocs = async (files) => {
    setUploading(true);
    try {
      const added = [];
      for (const f of Array.from(files).slice(0, 10 - docs.length)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        added.push(data.path);
      }
      setDocs((p) => [...p, ...added]);
      toast.success(`${added.length} document(s) uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (docs.length === 0) { toast.error("Please upload at least one identity/ownership document"); return; }
    if (!phone.trim() && !email.trim()) { toast.error("Confirm a phone number or email"); return; }
    setSubmitting(true);
    try {
      await api.post("/verification", { verification_role: role, docs, phone, email, note });
      toast.success("Submitted for review!");
      loadMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (me === null) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" /></div>;

  const sm = STATUS_META[me.verification_status] || STATUS_META.unverified;
  const StatusIcon = sm.icon;

  return (
    <div className="mx-auto max-w-2xl px-5 py-12 lg:px-8">
      <span className="label-caps text-[#D4A373]">Trust & safety</span>
      <h1 className="mt-2 font-display text-3xl font-light text-[#2C3830] sm:text-4xl">Get verified</h1>
      <p className="mt-3 text-sm text-[#6B7280]">
        Verified listers earn a trust badge that guests and buyers see — the best way to stand out and avoid being mistaken for a scam.
      </p>

      <div data-testid="verification-status" className={`mt-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${sm.cls}`}>
        <StatusIcon className="h-4 w-4" /> Status: <span className="font-semibold">{sm.label}</span>
        {me.verification_role && <span className="opacity-80">· {listerLabel(me.verification_role)}</span>}
      </div>
      {me.verification_status === "rejected" && me.admin_note && (
        <p className="mt-2 text-sm text-[#B91C1C]">Reviewer note: {me.admin_note}</p>
      )}

      <div className="mt-6 space-y-5 rounded-2xl border border-border/60 bg-white p-6">
        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">I am a</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="verify-role-select" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LISTER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Confirm phone</Label>
            <Input data-testid="verify-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0712 345 678" className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps text-[#6B7280]">Confirm email</Label>
            <Input data-testid="verify-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="rounded-xl" />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">Documents</Label>
          <p className="text-xs text-[#6B7280]">Upload your ID/passport, plus proof of ownership or authority (title deed, utility bill, management/authorization letter, or agency & KRA PIN certificate).</p>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => uploadDocs(e.target.files)} />
          <div className="flex flex-wrap gap-2">
            {docs.map((d, i) => (
              <span key={i} className="flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-xs text-[#2C3830]">
                <FileText className="h-3.5 w-3.5 text-[#D4A373]" />
                <a href={fileUrl(d)} target="_blank" rel="noreferrer" className="underline">Doc {i + 1}</a>
                <button data-testid={`remove-doc-${i}`} onClick={() => setDocs(docs.filter((_, idx) => idx !== i))}><X className="h-3.5 w-3.5 text-[#6B7280] hover:text-destructive" /></button>
              </span>
            ))}
            {docs.length < 10 && (
              <button data-testid="upload-doc-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-xl border-2 border-dashed border-border px-4 py-2 text-xs text-[#6B7280] hover:border-[#D4A373] hover:text-[#D4A373]">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload document
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="label-caps text-[#6B7280]">Anything else (optional)</Label>
          <Textarea data-testid="verify-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. agency name, number of units managed…" className="rounded-xl" />
        </div>

        <Button data-testid="submit-verification-btn" onClick={submit} disabled={submitting} className="w-full rounded-full bg-[#2C3830] py-6 text-base font-semibold text-white hover:bg-[#3A4A40]">
          {submitting ? "Submitting…" : me.verification_status === "pending" ? "Resubmit for review" : "Submit for verification"}
        </Button>
      </div>
    </div>
  );
}
